import { createBrowserRouter, Navigate } from 'react-router-dom';
import React, { Suspense } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import Layout from '../components/Layout';
import EmployeeLayout from '../components/EmployeeLayout';
import ErrorBoundary from '../components/ErrorBoundary';
import Spinner from '../components/Spinner';
import { useAppSelector } from '../app/hooks';

// ── Lazy-loaded pages ──
const DepartmentsPage = React.lazy(() => import('../features/departments/DepartmentsPage'));
const EmployeeDirectory = React.lazy(() => import('../features/employees/EmployeeDirectory'));
const CreateEmployeePage = React.lazy(() => import('../features/employees/CreateEmployeePage'));
const EmployeeProfile = React.lazy(() => import('../features/employees/EmployeeProfile'));
const TimeOffDashboard = React.lazy(() => import('../features/leaves/TimeOffDashboard'));
const LeaveRecords = React.lazy(() => import('../features/leaves/LeaveRecords'));
const HiringDashboard = React.lazy(() => import('../features/hiring/HiringDashboard'));
const ReportingDashboard = React.lazy(() => import('../features/reports/ReportingDashboard'));
const MasterCalendar = React.lazy(() => import('../pages/MasterCalendar'));
import Login from '../pages/Login';
import Register from '../pages/Register';
import VerifyOtp from '../pages/VerifyOtp';
import SetPassword from '../pages/SetPassword';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';

const Dashboard = React.lazy(() => import('../features/dashboard/Dashboard'));
const OrgManagementPage = React.lazy(() => import('../features/org/OrgManagementPage'));
const ESSDashboard = React.lazy(() => import('../features/ess/ESSDashboard'));
const ColleaguesDirectory = React.lazy(() => import('../features/ess/ColleaguesDirectory'));
const RequestCenter = React.lazy(() => import('../features/ess/RequestCenter'));
const MyInfo = React.lazy(() => import('../features/ess/MyInfo'));
const MyDocuments = React.lazy(() => import('../features/ess/MyDocuments'));
const MyLetters = React.lazy(() => import('../features/ess/MyLetters'));
const CalendarPage = React.lazy(() => import('../features/ess/CalendarPage'));
const MyAttendance = React.lazy(() => import('../features/ess/MyAttendance'));
const MyPayslips = React.lazy(() => import('../features/ess/MyPayslips'));
const MyLeaves = React.lazy(() => import('../features/ess/MyLeaves'));
const MyAssets = React.lazy(() => import('../features/ess/MyAssets'));
const SalaryManagement = React.lazy(() => import('../features/payroll/components/SalaryManagement'));
const EmployeeSalaryInfo = React.lazy(() => import('../features/payroll/components/EmployeeSalaryInfo'));
const PayrollDashboard = React.lazy(() => import('../features/payroll/components/PayrollDashboard'));
const SalaryAdvanceRequest = React.lazy(() => import('../features/ess/components/SalaryAdvanceRequest'));
const OvertimeTracking = React.lazy(() => import('../features/payroll/components/OvertimeTracking'));
const ExpenseDashboard = React.lazy(() => import('../features/expenses/pages/ExpenseDashboard'));
const LoanDashboard = React.lazy(() => import('../features/loans/pages/LoanDashboard'));
const IncrementDashboard = React.lazy(() => import('../features/payroll/components/IncrementDashboard'));
const IncrementCycleDetail = React.lazy(() => import('../features/payroll/components/IncrementCycleDetail'));
const TrainingDashboard = React.lazy(() => import('../features/training/TrainingDashboard'));
const PerformanceDashboard = React.lazy(() => import('../features/performance/PerformanceDashboard'));
const GlobalSettings = React.lazy(() => import('../features/settings/GlobalSettings'));
const BiometricIntegrationPage = React.lazy(() => import('../features/biometric/BiometricIntegrationPage'));
const DataManagementPage = React.lazy(() => import('../features/settings/DataManagementPage'));
const SecuritySettings = React.lazy(() => import('../features/security/SecurityCenter'));
const AttendanceDashboard = React.lazy(() => import('../features/attendance/AttendanceDashboard'));
const OvertimeShiftsDashboard = React.lazy(() => import('../features/attendance/OvertimeShiftsDashboard'));
const AirTicketDashboard = React.lazy(() => import('../features/airTickets/AirTicketDashboard'));
const LeaveSalaryPage = React.lazy(() => import('../features/leaveSalary/LeaveSalaryPage'));
const ShiftSchedulerPage = React.lazy(() => import('../features/shifts/ShiftSchedulerPage'));
const BranchesPage = React.lazy(() => import('../features/branches/BranchesPage'));
const AssetsPage = React.lazy(() => import('../features/assets/AssetsPage'));
const BenefitsPage = React.lazy(() => import('../features/benefits/BenefitsPage'));
const CompanyDocumentsPage = React.lazy(() => import('../features/documents/CompanyDocumentsPage'));
const TemplateLibraryPage = React.lazy(() => import('../features/documents/TemplateLibraryPage'));
const GeneratedDocumentsPage = React.lazy(() => import('../features/documents/GeneratedDocumentsPage'));

const PendingApprovalsDashboard = React.lazy(() => import('../features/approvals/PendingApprovalsDashboard'));
const ComplianceHub = React.lazy(() => import('../features/compliance/ComplianceHub'));
const LeaveApplication = React.lazy(() => import('../features/leaves/LeaveApplication'));
const LeaveApprovals = React.lazy(() => import('../features/leaves/LeaveApprovals'));
const PayrollBatchSlips = React.lazy(() => import('../features/payroll/components/PayrollBatchSlips'));
const SalaryHistoryPage = React.lazy(() => import('../features/payroll/components/SalaryHistoryPage'));
const AdminDashboard = React.lazy(() => import('../pages/AdminDashboard'));
const Unauthorized = () => <div className="flex items-center justify-center h-screen">Unauthorized Access</div>;
const NotFound = () => <div className="flex items-center justify-center h-screen">404 Not Found</div>;

const PageLoader = () => (
    <div className="flex justify-center items-center h-full min-h-[400px]">
        <Spinner size="lg" />
    </div>
);

// ── Role constants ──
const ADMIN_ROLES = ['hr_admin', 'admin', 'hr', 'manager', 'super_admin'];
const ALL_ROLES = [...ADMIN_ROLES, 'employee'];

// Employees → /ess/dashboard, everyone else → /dashboard
const RoleBasedRedirect = () => {
    const user = useAppSelector((state) => state.auth.user);
    const isEmployee = user?.role?.toLowerCase() === 'employee';
    return <Navigate to={isEmployee ? '/ess/dashboard' : '/dashboard'} replace />;
};

const DynamicLayout = () => {
    const user = useAppSelector((state) => state.auth.user);
    const isEmployee = user?.role?.toLowerCase() === 'employee';
    return isEmployee ? <EmployeeLayout /> : <Layout />;
};

// ── Suspense wrapper helper ──
const lazy = (Component: React.LazyExoticComponent<any>) => (
    <Suspense fallback={<PageLoader />}>
        <Component />
    </Suspense>
);

const router = createBrowserRouter([
    // ── Public routes ──
    { path: '/login', element: <Login />, errorElement: <ErrorBoundary><div /></ErrorBoundary> },
    { path: '/register', element: <Register /> },
    { path: '/verify-otp', element: <VerifyOtp /> },
    { path: '/set-password', element: <SetPassword /> },
    { path: '/forgot-password', element: <ForgotPassword /> },
    { path: '/reset-password', element: <ResetPassword /> },
    { path: '/unauthorized', element: <Unauthorized /> },
    { path: '/admin', element: lazy(AdminDashboard) },

    // ── Protected routes (auth check only) ──
    {
        element: <ProtectedRoute />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
        children: [
            // Root → role-based redirect
            { path: '/', element: <RoleBasedRedirect /> },

            // ═══════════════════════════════════════════
            //  DASHBOARD — open to any admin-viewMode user (matches Sidebar's
            //  Dashboard item, which has no role/permission restriction).
            //  Must NOT sit behind ADMIN_ROLES: a custom/limited role (e.g. a
            //  department head) lands here right after login via
            //  RoleBasedRedirect, so gating it would 404/unauthorize them
            //  immediately after signing in.
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ALL_ROLES} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/dashboard', element: lazy(Dashboard) },
                        ],
                    },
                ],
            },

            // ═══════════════════════════════════════════
            //  ADMIN ROUTES — blocked for EMPLOYEE role. No matching
            //  permission code exists for these modules yet, so they stay
            //  gated purely by built-in role (not delegable via a Security
            //  Center checkbox).
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/departments', element: lazy(DepartmentsPage) },
                            { path: '/employees/create', element: lazy(CreateEmployeePage) },
                            { path: '/calendar', element: lazy(MasterCalendar) },
                            { path: '/reports', element: lazy(ReportingDashboard) },
                            { path: '/settings/org', element: lazy(OrgManagementPage) },
                            { path: '/settings/security', element: lazy(SecuritySettings) },
                            { path: '/settings/data-management', element: lazy(DataManagementPage) },
                            { path: '/expenses', element: lazy(ExpenseDashboard) },
                            { path: '/branches', element: lazy(BranchesPage) },
                            { path: '/assets', element: lazy(AssetsPage) },
                            { path: '/benefits', element: lazy(BenefitsPage) },
                            { path: '/compliance', element: lazy(ComplianceHub) },
                        ],
                    },
                ],
            },

            // ═══════════════════════════════════════════
            //  LEAVES / ATTENDANCE / DOCUMENTS / GLOBAL SETTINGS — admin
            //  roles OR the matching permission granted via Security Center,
            //  mirroring the same itemPermission map in Sidebar.tsx so a
            //  visible sidebar link never 404s/unauthorizes on click.
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission={['leaves:view', 'leaves:manage']} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/leaves', element: lazy(TimeOffDashboard) },
                            { path: '/leaves/records', element: lazy(LeaveRecords) },
                        ],
                    },
                ],
            },
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission={['attendance:view', 'attendance:log']} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/attendance', element: lazy(AttendanceDashboard) },
                            { path: '/attendance/overtime', element: lazy(OvertimeShiftsDashboard) },
                            { path: '/air-tickets', element: lazy(AirTicketDashboard) },
                        ],
                    },
                ],
            },
            {
                // Leave Salary is payroll-adjacent (encashment/leave-salary
                // amounts) and deliberately NOT reachable via the shared
                // attendance:view/log permission the three pages above use -
                // a role like Department Head that only has that box ticked
                // in Security Center must not get this one too.
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission={['attendance:view', 'attendance:log']} excludeRoles={['department_head']} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/leave-salary', element: lazy(LeaveSalaryPage) },
                        ],
                    },
                ],
            },
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission="documents:view" />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/settings/documents', element: lazy(CompanyDocumentsPage) },
                            { path: '/documents/templates', element: lazy(TemplateLibraryPage) },
                            { path: '/documents/generated', element: lazy(GeneratedDocumentsPage) },
                        ],
                    },
                ],
            },
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission={['settings:manage', 'settings:edit']} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/settings/global', element: lazy(GlobalSettings) },
                            { path: '/settings/biometric', element: lazy(BiometricIntegrationPage) },
                        ],
                    },
                ],
            },


            // ═══════════════════════════════════════════
            //  ROSTERING — admin roles OR anyone explicitly granted the
            //  rostering:view / rostering:manage permission via Security
            //  Center (e.g. a department-head manager, whose collapsed
            //  `role` is 'employee' and would otherwise never pass
            //  ADMIN_ROLES alone).
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission={['rostering:view', 'rostering:manage']} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/rostering', element: lazy(ShiftSchedulerPage) },
                        ],
                    },
                ],
            },

            // ═══════════════════════════════════════════
            //  APPROVALS — admin roles OR approvals:view / approvals:manage
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission={['approvals:view', 'approvals:manage']} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/approvals', element: lazy(PendingApprovalsDashboard) },
                            { path: '/requests/approvals', element: lazy(LeaveApprovals) },
                        ],
                    },
                ],
            },

            // ═══════════════════════════════════════════
            //  PAYROLL & FINANCE — admin roles OR ONE consolidated
            //  payroll:view / payroll:process permission for the whole
            //  section (Payroll Hub, Payroll History, Salary Revision &
            //  Arrears, Adjustments & Loans) - not gated item-by-item.
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission={['payroll:view', 'payroll:process']} />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            {
                                path: '/payroll',
                                children: [
                                    { index: true, element: lazy(SalaryManagement) },
                                    { path: 'dashboard', element: lazy(PayrollDashboard) },
                                    { path: 'run', element: lazy(PayrollDashboard) },
                                    { path: 'overtime', element: lazy(OvertimeTracking) },
                                    { path: 'employee/:employeeId', element: lazy(EmployeeSalaryInfo) },
                                    { path: 'batches/:batchId/slips', element: lazy(PayrollBatchSlips) },
                                    { path: 'history', element: lazy(SalaryHistoryPage) },
                                ],
                            },
                            { path: '/loans', element: lazy(LoanDashboard) },
                            {
                                path: '/increments',
                                children: [
                                    { index: true, element: lazy(IncrementDashboard) },
                                    { path: 'cycles/:id', element: lazy(IncrementCycleDetail) },
                                ],
                            },
                        ],
                    },
                ],
            },

            // ═══════════════════════════════════════════
            //  TALENT MANAGEMENT — admin roles OR ONE consolidated
            //  talent:view permission for the whole section (Hiring,
            //  Training, Performance) - not gated item-by-item.
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ADMIN_ROLES} requiredPermission="talent:view" />,
                children: [
                    {
                        element: <Layout />,
                        children: [
                            { path: '/hiring', element: lazy(HiringDashboard) },
                            { path: '/training', element: lazy(TrainingDashboard) },
                            { path: '/performance', element: lazy(PerformanceDashboard) },
                        ],
                    },
                ],
            },

            // ═══════════════════════════════════════════
            //  DYNAMIC ROUTE BLOCK — accessible by all roles, dynamically selecting layout
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ALL_ROLES} />,
                children: [
                    {
                        element: <DynamicLayout />,
                        children: [
                            { path: '/employees', element: lazy(EmployeeDirectory) },
                            { path: '/employees/:id', element: lazy(EmployeeProfile) },
                        ],
                    },
                ],
            },

            // ═══════════════════════════════════════════
            //  EMPLOYEE ROUTES — accessible by all roles
            // ═══════════════════════════════════════════
            {
                element: <ProtectedRoute allowedRoles={ALL_ROLES} />,
                children: [
                    {
                        element: <EmployeeLayout />,
                        children: [
                            { path: '/ess/dashboard', element: lazy(ESSDashboard) },
                            { path: '/ess/colleagues', element: lazy(ColleaguesDirectory) },
                            { path: '/ess/attendance', element: lazy(MyAttendance) },
                            { path: '/ess/leaves', element: lazy(MyLeaves) },
                            { path: '/ess/leave/apply', element: lazy(LeaveApplication) },
                            { path: '/ess/calendar', element: lazy(CalendarPage) },
                            { path: '/ess/payslips', element: lazy(MyPayslips) },
                            { path: '/ess/advance-request', element: lazy(SalaryAdvanceRequest) },
                            { path: '/ess/profile', element: lazy(MyInfo) },
                            { path: '/ess/documents', element: lazy(MyDocuments) },
                            { path: '/ess/letters', element: lazy(MyLetters) },
                            { path: '/ess/requests', element: lazy(RequestCenter) },
                            { path: '/ess/assets', element: lazy(MyAssets) },

                        ],
                    },
                ],
            },
        ],
    },
    { path: '*', element: <NotFound /> },
]);

export default router;
